import type { StrategyNode } from './BaseNode';
import { v4 as uuid } from 'uuid';
import { isStrategyNode } from './typeGuards';
import { reinstantiate } from '../utils/nodeFactory';

/**
 * RegressionNode: Generates a signal based on a regression model (e.g. Ridge) 
 * over multiple input features.
 */
export class RegressionNode implements StrategyNode {
    id: string;
    type: 'Regression' = 'Regression';
    title?: string;
    isManualRename?: boolean;
    isMicrostructure = true;
    isPayloadAware = true;
    config: {
        model_type: 'ridge' | 'linear';
        weights: number[];
        inputs: StrategyNode[];
        last_calibrated_at?: string;
        calibration_artifact_id?: string;
        calibration_artifact_path?: string;
        feature_tree_hash?: string;
        feature_labels?: string[];
    };

    constructor(
        id: string,
        title?: string,
        model_type: 'ridge' | 'linear' = 'ridge',
        weights: number[] = [],
        inputs: StrategyNode[] = [],
        isManualRename?: boolean,
        last_calibrated_at?: string,
        calibration_artifact_id?: string,
        calibration_artifact_path?: string,
        feature_tree_hash?: string,
        feature_labels?: string[]
    ) {
        this.id = id;
        this.title = title || 'Regression Model';
        this.isManualRename = isManualRename;
        
        // Re-instantiate any raw input objects so they always have methods
        const finalInputs = (inputs || []).map(input => reinstantiate(input));

        this.config = {
            model_type,
            weights,
            inputs: finalInputs,
            last_calibrated_at,
            calibration_artifact_id,
            calibration_artifact_path,
            feature_tree_hash,
            feature_labels
        };
    }

    update(id: string, patch: Partial<StrategyNode>): StrategyNode {
        if (this.id === id) {
            // Self-update
            const incomingConfig = (patch as any)?.config || {};
            const mergedConfig = { 
                ...this.config, 
                ...incomingConfig,
                // Ensure inputs and weights are preserved even if patch doesn't include them
                inputs: incomingConfig.inputs !== undefined ? incomingConfig.inputs : this.config.inputs,
                weights: incomingConfig.weights !== undefined ? incomingConfig.weights : this.config.weights,
                last_calibrated_at: incomingConfig.last_calibrated_at !== undefined ? incomingConfig.last_calibrated_at : this.config.last_calibrated_at,
                calibration_artifact_id: incomingConfig.calibration_artifact_id !== undefined ? incomingConfig.calibration_artifact_id : this.config.calibration_artifact_id,
                calibration_artifact_path: incomingConfig.calibration_artifact_path !== undefined ? incomingConfig.calibration_artifact_path : this.config.calibration_artifact_path,
                feature_tree_hash: incomingConfig.feature_tree_hash !== undefined ? incomingConfig.feature_tree_hash : this.config.feature_tree_hash,
                feature_labels: incomingConfig.feature_labels !== undefined ? incomingConfig.feature_labels : this.config.feature_labels,
            };

            return new RegressionNode(
                this.id,
                patch.title || this.title,
                mergedConfig.model_type,
                mergedConfig.weights,
                mergedConfig.inputs,
                patch.isManualRename ?? this.isManualRename,
                mergedConfig.last_calibrated_at,
                mergedConfig.calibration_artifact_id,
                mergedConfig.calibration_artifact_path,
                mergedConfig.feature_tree_hash,
                mergedConfig.feature_labels
            );
        }

        // Recursively update inputs
        let changed = false;
        const updatedInputs = this.config.inputs.map(input => {
            const updated = input.update(id, patch);
            if (updated !== input) {
                changed = true;
                return updated;
            }
            return input;
        });

        if (changed) {
            return new RegressionNode(
                this.id,
                this.title,
                this.config.model_type,
                this.config.weights,
                updatedInputs,
                this.isManualRename,
                this.config.last_calibrated_at,
                this.config.calibration_artifact_id,
                this.config.calibration_artifact_path,
                this.config.feature_tree_hash,
                this.config.feature_labels
            );
        }

        return this;
    }

    clone(): StrategyNode {
        return new RegressionNode(
            uuid(),
            this.title,
            this.config.model_type,
            [...this.config.weights],
            this.config.inputs.map(input => isStrategyNode(input) ? input.clone() : input),
            this.isManualRename,
            this.config.last_calibrated_at,
            this.config.calibration_artifact_id,
            this.config.calibration_artifact_path,
            this.config.feature_tree_hash,
            this.config.feature_labels ? [...this.config.feature_labels] : undefined
        );
    }
}
